const STORAGE_KEY = "theme:size-region";
const VALID_REGIONS = new Set(["US", "EU", "UK", "CM"]);
const SIZE_LABEL_SELECTOR = "[data-size-label]";
const SIZE_REGION_CONTROL_SELECTOR = "[data-size-region-control]";
const SIZE_SELECTED_VALUE_SELECTOR = '[id$="-selected-value"][data-size-label]';
const SIZE_REGION_LABEL_SELECTOR = "[data-size-region-label]";

function normalizeRegion(value) {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value).trim().toUpperCase();
  return VALID_REGIONS.has(normalizedValue) ? normalizedValue : null;
}

function getCountryDefaultRegion() {
  const countryCode = String(window.themeVariables?.settings?.countryCode || "").trim().toUpperCase();

  if (countryCode === "US" || countryCode === "CA") {
    return "US";
  }

  if (countryCode === "GB") {
    return "UK";
  }

  return "EU";
}

function readStoredRegion() {
  try {
    return normalizeRegion(window.localStorage.getItem(STORAGE_KEY));
  } catch (error) {
    return null;
  }
}

function writeStoredRegion(region) {
  try {
    window.localStorage.setItem(STORAGE_KEY, region);
  } catch (error) {
    // Intentionally ignored if storage is blocked.
  }
}

function isRadioRegionControl(control) {
  return control instanceof HTMLInputElement && control.type === "radio";
}

function getCheckedRadioRegionControl(control) {
  if (!isRadioRegionControl(control)) {
    return null;
  }

  const parentScope = control.closest(".variant-picker__size-dropdown-row") || document;
  const controlsInScope = Array.from(parentScope.querySelectorAll(SIZE_REGION_CONTROL_SELECTOR)).filter(
    (candidate) => isRadioRegionControl(candidate) && candidate.name === control.name
  );

  return controlsInScope.find((candidate) => candidate.checked) || controlsInScope[0] || control;
}

function getRegionFromControl(control) {
  if (!control) {
    return null;
  }

  if (isRadioRegionControl(control)) {
    const checkedControl = getCheckedRadioRegionControl(control);
    return normalizeRegion(checkedControl?.value) || normalizeRegion(control.dataset.sizeRegionDefault);
  }

  return normalizeRegion(control.value) || normalizeRegion(control.dataset.sizeRegionDefault);
}

function resolveDefaultRegionFromControls() {
  const control = document.querySelector(SIZE_REGION_CONTROL_SELECTOR);

  if (!control) {
    return null;
  }

  return getRegionFromControl(control);
}

function resolveActiveRegion() {
  return readStoredRegion() || resolveDefaultRegionFromControls() || getCountryDefaultRegion();
}

function getLabelValueForRegion(element, region) {
  const legacySizeValues = extractLegacySizeValues(element.dataset.sizeFallback);

  switch (region) {
    case "US":
      return element.dataset.sizeUs || legacySizeValues?.US;
    case "UK":
      return element.dataset.sizeUk || legacySizeValues?.UK;
    case "CM":
      return element.dataset.sizeCm || legacySizeValues?.CM;
    case "EU":
    default:
      return element.dataset.sizeEu || legacySizeValues?.EU;
  }
}

function cleanLegacyValue(value) {
  if (!value) {
    return null;
  }

  const cleaned = String(value)
    .replace(/^[\s:,|\\/\-]+|[\s:,|\\/\-]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || null;
}

function extractLegacySizeValues(fallbackLabel) {
  if (!fallbackLabel) {
    return null;
  }

  const label = String(fallbackLabel);
  const numericValues = {
    US: cleanLegacyValue(label.match(/\bUS\b\s*([0-9]+(?:[.,][0-9]+)?(?:\s+[0-9]+\/[0-9]+)?)/i)?.[1]),
    EU: cleanLegacyValue(label.match(/\bEU\b\s*([0-9]+(?:[.,][0-9]+)?(?:\s+[0-9]+\/[0-9]+)?)/i)?.[1]),
    UK: cleanLegacyValue(label.match(/\bUK\b\s*([0-9]+(?:[.,][0-9]+)?(?:\s+[0-9]+\/[0-9]+)?)/i)?.[1]),
    CM: cleanLegacyValue(label.match(/\b(?:CM|IN\s*CM)\b\s*([0-9]+(?:[.,][0-9]+)?)/i)?.[1])
  };

  if (numericValues.US || numericValues.EU || numericValues.UK || numericValues.CM) {
    return numericValues;
  }

  const chunkValues = {
    US: cleanLegacyValue(label.match(/\bUS\b\s*([^\n\r]+?)(?=\s+\bEU\b|$)/i)?.[1]),
    EU: cleanLegacyValue(label.match(/\bEU\b\s*([^\n\r]+?)(?=\s+\bUK\b|$)/i)?.[1]),
    UK: cleanLegacyValue(label.match(/\bUK\b\s*([^\n\r]+?)(?=\s+\bCM\b|\s+\bIN\s*CM\b|$)/i)?.[1]),
    CM: cleanLegacyValue(label.match(/\b(?:CM|IN\s*CM)\b\s*([^\n\r]+?)$/i)?.[1])
  };

  if (!chunkValues.US && !chunkValues.EU && !chunkValues.UK && !chunkValues.CM) {
    return null;
  }

  return chunkValues;
}

function syncAnimatedButtonText(element, nextValue) {
  const buttonContent = element.closest("button-content");

  if (!buttonContent) {
    return;
  }

  if (buttonContent.hasAttribute("rotated-text")) {
    buttonContent.setAttribute("rotated-text", nextValue);
  }

  if (buttonContent.hasAttribute("up-text")) {
    buttonContent.setAttribute("up-text", nextValue);
  }
}

function syncDropdownSelectedValueFromCheckedOption(selectedElement, region) {
  const triggerButton = selectedElement.closest("button[aria-controls]");
  const popoverId = triggerButton?.getAttribute("aria-controls");

  if (!popoverId) {
    return;
  }

  const popover = document.getElementById(popoverId);
  const checkedInput = popover?.querySelector('input[type="radio"][data-option-position]:checked');
  const checkedOptionLabel = checkedInput?.closest(".popover__value-option")?.querySelector(SIZE_LABEL_SELECTOR);

  if (!checkedOptionLabel) {
    return;
  }

  const nextValue = getLabelValueForRegion(checkedOptionLabel, region) || checkedOptionLabel.dataset.sizeFallback;

  if (!nextValue || selectedElement.textContent === nextValue) {
    return;
  }

  selectedElement.textContent = nextValue;
  syncAnimatedButtonText(selectedElement, nextValue);
}

function inspectSizeSources(root = document) {
  const labels = Array.from(root.querySelectorAll(SIZE_LABEL_SELECTOR));
  const rows = labels.map((element) => {
    const source = element.dataset.sizeSource || "unknown";
    const fallback = element.dataset.sizeFallback || null;
    const currentLabel = (element.textContent || "").trim();
    const rowId = element.dataset.sizeRowId || null;
    const usReference = element.dataset.sizeUsReference || null;

    return {
      source,
      currentLabel,
      fallback,
      rowId,
      usReference,
      hasUs: Boolean(element.dataset.sizeUs),
      hasEu: Boolean(element.dataset.sizeEu),
      hasUk: Boolean(element.dataset.sizeUk),
      hasCm: Boolean(element.dataset.sizeCm),
      element
    };
  });

  const summary = rows.reduce((accumulator, row) => {
    accumulator[row.source] = (accumulator[row.source] || 0) + 1;
    return accumulator;
  }, {});

  console.groupCollapsed("[size-region] Source inspection");
  console.info("Active region:", resolveActiveRegion());
  console.table(summary);
  console.table(
    rows.map((row) => ({
      source: row.source,
      currentLabel: row.currentLabel,
      fallback: row.fallback,
      rowId: row.rowId,
      usReference: row.usReference,
      hasUs: row.hasUs,
      hasEu: row.hasEu,
      hasUk: row.hasUk,
      hasCm: row.hasCm
    }))
  );
  console.groupEnd();

  return { summary, rows };
}

function inspectProductMetaobjectDebug(root = document) {
  const debugScript = root.querySelector("script[data-size-region-debug]");

  if (!debugScript) {
    console.warn("[size-region] No debug script found in markup.");
    return null;
  }

  try {
    const parsed = JSON.parse(debugScript.textContent);
    console.groupCollapsed("[size-region] Metaobject debug");
    console.table(parsed);
    console.groupEnd();
    return parsed;
  } catch (error) {
    console.error("[size-region] Could not parse debug script.", error);
    return null;
  }
}

function applyRegion(region, root = document) {
  const normalizedRegion = normalizeRegion(region) || "EU";

  root.querySelectorAll(SIZE_LABEL_SELECTOR).forEach((element) => {
    const nextValue = getLabelValueForRegion(element, normalizedRegion) || element.dataset.sizeFallback;

    if (!nextValue) {
      return;
    }

    if (element.textContent !== nextValue) {
      element.textContent = nextValue;
      syncAnimatedButtonText(element, nextValue);
    }
  });

  root.querySelectorAll(SIZE_REGION_CONTROL_SELECTOR).forEach((control) => {
    if (isRadioRegionControl(control)) {
      control.checked = normalizeRegion(control.value) === normalizedRegion;
      return;
    }

    if (control.value !== normalizedRegion) {
      control.value = normalizedRegion;
    }
  });

  root.querySelectorAll(SIZE_REGION_LABEL_SELECTOR).forEach((label) => {
    if (label.textContent !== normalizedRegion) {
      label.textContent = normalizedRegion;
    }
  });

  root.querySelectorAll(SIZE_SELECTED_VALUE_SELECTOR).forEach((selectedElement) => {
    syncDropdownSelectedValueFromCheckedOption(selectedElement, normalizedRegion);
  });
}

function shouldSyncForNode(node) {
  if (!(node instanceof Element)) {
    return false;
  }

  return (
    node.matches(SIZE_LABEL_SELECTOR) ||
    node.matches(SIZE_REGION_CONTROL_SELECTOR) ||
    node.querySelector(SIZE_LABEL_SELECTOR) !== null ||
    node.querySelector(SIZE_REGION_CONTROL_SELECTOR) !== null
  );
}

function initSizeRegionSync() {
  applyRegion(resolveActiveRegion(), document);

  document.addEventListener("change", (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const control = event.target.closest(SIZE_REGION_CONTROL_SELECTOR);

    if (!control) {
      return;
    }

    const region = getRegionFromControl(control) || getCountryDefaultRegion();
    writeStoredRegion(region);
    applyRegion(region, document);
    document.dispatchEvent(new CustomEvent("theme:size-region:change", { detail: { region } }));
  });

  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    applyRegion(normalizeRegion(event.newValue) || getCountryDefaultRegion(), document);
  });

  let syncQueued = false;

  const scheduleSync = () => {
    if (syncQueued) {
      return;
    }

    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      applyRegion(resolveActiveRegion(), document);
    });
  };

  const observer = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      for (const addedNode of mutation.addedNodes) {
        if (shouldSyncForNode(addedNode)) {
          scheduleSync();
          return;
        }
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSizeRegionSync, { once: true });
} else {
  initSizeRegionSync();
}

window.themeSizeRegionDebug = {
  inspectSizeSources,
  inspectProductMetaobjectDebug
};
